import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

const IDLE_TIMEOUT_MS = 60 * 60 * 1000;  // 60 minutes
const WARNING_BEFORE_MS = 5 * 60 * 1000; // 5 minute warning
const IDLE_EVENTS = ['mousemove', 'mousedown', 'keypress', 'touchstart', 'scroll'] as const;

interface UseIdleTimeoutResult {
  showWarning: boolean;
  secondsRemaining: number;
  stayLoggedIn: () => void;
}

export function useIdleTimeout(onTimeout: () => void): UseIdleTimeoutResult {
  const [showWarning, setShowWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(WARNING_BEFORE_MS / 1000);

  const warningTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const timeoutTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const countdownRef = useRef<ReturnType<typeof setInterval> | undefined>(undefined);
  const onTimeoutRef = useRef(onTimeout);
  const lastActivityRef = useRef<number>(Date.now());
  onTimeoutRef.current = onTimeout;

  const clearTimers = useCallback(() => {
    clearTimeout(warningTimerRef.current);
    clearTimeout(timeoutTimerRef.current);
    clearInterval(countdownRef.current);
  }, []);

  const startCountdown = useCallback(() => {
    setSecondsRemaining(WARNING_BEFORE_MS / 1000);
    clearInterval(countdownRef.current);
    countdownRef.current = setInterval(() => {
      setSecondsRemaining(s => {
        if (s <= 1) {
          clearInterval(countdownRef.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }, []);

  const reset = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    clearTimers();

    warningTimerRef.current = setTimeout(() => {
      setShowWarning(true);
      startCountdown();
    }, IDLE_TIMEOUT_MS - WARNING_BEFORE_MS);

    timeoutTimerRef.current = setTimeout(() => {
      onTimeoutRef.current();
    }, IDLE_TIMEOUT_MS);
  }, [clearTimers, startCountdown]);

  // Start timer and attach activity listeners
  useEffect(() => {
    const handler = () => reset();
    IDLE_EVENTS.forEach(e => window.addEventListener(e, handler, { passive: true }));
    reset();
    return () => {
      IDLE_EVENTS.forEach(e => window.removeEventListener(e, handler));
      clearTimers();
    };
  }, [reset, clearTimers]);

  // When the laptop wakes from sleep, JS timers resume from where they paused rather
  // than firing for elapsed wall-clock time. Check real elapsed time on tab visibility
  // change and log out immediately if the idle threshold has passed.
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return;
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= IDLE_TIMEOUT_MS) {
        clearTimers();
        onTimeoutRef.current();
      } else if (elapsed >= IDLE_TIMEOUT_MS - WARNING_BEFORE_MS) {
        const remaining = Math.ceil((IDLE_TIMEOUT_MS - elapsed) / 1000);
        setShowWarning(true);
        setSecondsRemaining(remaining);
        clearInterval(countdownRef.current);
        countdownRef.current = setInterval(() => {
          setSecondsRemaining(s => {
            if (s <= 1) { clearInterval(countdownRef.current); return 0; }
            return s - 1;
          });
        }, 1000);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [clearTimers]);

  const stayLoggedIn = useCallback(() => {
    reset();
  }, [reset]);

  return { showWarning, secondsRemaining, stayLoggedIn };
}

// ── Warning modal ─────────────────────────────────────────────────────────────

interface Props {
  secondsRemaining: number;
  onStay: () => void;
  onLogout: () => void;
}

export default function IdleTimeoutWarning({ secondsRemaining, onStay, onLogout }: Props) {
  const minutes = Math.floor(secondsRemaining / 60);
  const seconds = secondsRemaining % 60;
  const timeStr =
    minutes > 0
      ? `${minutes}:${String(seconds).padStart(2, '0')}`
      : `${seconds}s`;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4">
      <div className="bg-dark-card border border-dark-border rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
        <div className="flex justify-center mb-4">
          <div className="p-3 bg-yellow-500/10 rounded-full">
            <ExclamationTriangleIcon className="w-7 h-7 text-yellow-400" />
          </div>
        </div>

        <h2 className="text-white font-semibold text-lg mb-2">Still there?</h2>
        <p className="text-gray-400 text-sm mb-1">
          You've been inactive for a while.
        </p>
        <p className="text-gray-300 text-sm mb-6">
          You'll be logged out in{' '}
          <span className="text-yellow-400 font-semibold tabular-nums">{timeStr}</span>.
        </p>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onLogout}
            className="flex-1 px-4 py-2 text-sm text-gray-400 hover:text-gray-200 border border-dark-border rounded-lg transition-colors"
          >
            Log out
          </button>
          <button
            type="button"
            onClick={onStay}
            className="flex-1 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg text-sm font-medium transition-colors"
          >
            Stay logged in
          </button>
        </div>
      </div>
    </div>
  );
}
